import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Plus, Trash2, X } from 'lucide-react';
import { AppShell, PageSection } from '@/components/app/AppShell';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { accountsApi, AccountNode, StatutoryCategory } from '@/lib/accounts';
import { managementPlApi, PlRow } from '@/lib/managementPl';
import { forecastsApi } from '@/lib/forecasts';
import { companiesApi } from '@/lib/companies';
import { api } from '@/lib/api';
import { ConfirmModal } from '@/components/ConfirmModal';

const today = new Date().toISOString().slice(0, 10);
const oneYearAgo = `${Number(today.slice(0, 4)) - 1}${today.slice(4)}`;

export function PLPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

  // Default reporting currency = the company's reporting currency.
  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<{ companyId: string }>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });
  const { data: companies = [] } = useQuery({ queryKey: ['companies'], queryFn: () => companiesApi.list() });
  const company = companies.find((c) => c.id === workspace?.companyId);

  const { data: scenarios = [] } = useQuery({
    queryKey: ['scenarios', wsId],
    queryFn: () => forecastsApi.scenarios(wsId!),
    enabled: !!wsId,
  });
  const scenarioOpts = useMemo(() => {
    const s = new Set<string>(['budget', 'actual']);
    scenarios.forEach((x) => s.add(x.scenario));
    return Array.from(s);
  }, [scenarios]);

  const [periodStart, setPeriodStart] = useState(oneYearAgo);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [scenarioA, setScenarioA] = useState('budget');
  const [scenarioB, setScenarioB] = useState('actual');
  const [bva, setBva] = useState(true);
  const [consolidated, setConsolidated] = useState(false);
  const [view, setView] = useState<'management' | 'statutory'>('management');
  const [currency, setCurrency] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [drill, setDrill] = useState<{ code: string; scenario: string; label: string } | null>(null);

  const reportingCurrency = currency || company?.reportingCurrency || 'USD';

  const pl = useQuery({
    queryKey: ['management-pl', wsId, periodStart, periodEnd, scenarioA, bva ? scenarioB : '', consolidated, reportingCurrency, view],
    queryFn: () => managementPlApi.get(wsId!, {
      scenarioA, scenarioB: bva ? scenarioB : undefined, periodStart, periodEnd,
      reportingCurrency, consolidated, view,
    }),
    enabled: !!wsId && !!periodStart && !!periodEnd,
    retry: false,
  });
  const planErr = pl.error
    ? ((pl.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (pl.error as Error).message)
    : null;

  const inputCls = 'rounded-md border border-border bg-white px-2.5 py-1.5 text-xs';

  return (
    <AppShell title={t('pl.title', { defaultValue: 'Management P&L' })} subtitle={t('pl.subtitle', { defaultValue: 'Structured budget-vs-actual P&L, consolidated across entities.' })}>
      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-5">
        <div>
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('pl.title', { defaultValue: 'Management P&L' })}</h1>
          <p className="mt-1 text-muted-foreground">{t('pl.subtitle', { defaultValue: 'Structured budget-vs-actual P&L, consolidated across entities.' })}</p>
        </div>

        {/* Control bar */}
        <div className="rounded-md border border-border bg-card p-3 flex flex-wrap items-end gap-3 sticky top-16 z-10">
          <Field label={t('pl.period_start', { defaultValue: 'From' })}>
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={`num ${inputCls}`} />
          </Field>
          <Field label={t('pl.period_end', { defaultValue: 'To' })}>
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={`num ${inputCls}`} />
          </Field>
          <Field label={t('pl.scenario_a', { defaultValue: 'A (budget)' })}>
            <select value={scenarioA} onChange={(e) => setScenarioA(e.target.value)} className={inputCls}>
              {scenarioOpts.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-1.5 text-xs text-ink-700 pb-1.5">
            <input type="checkbox" checked={bva} onChange={(e) => setBva(e.target.checked)} className="h-3.5 w-3.5 rounded border-border" />
            {t('pl.compare', { defaultValue: 'Compare B' })}
          </label>
          {bva && (
            <Field label={t('pl.scenario_b', { defaultValue: 'B (actual)' })}>
              <select value={scenarioB} onChange={(e) => setScenarioB(e.target.value)} className={inputCls}>
                {scenarioOpts.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          )}
          {/* Management ↔ statutory presentation toggle (same facts, two groupings). */}
          <div className="inline-flex rounded-md border border-border p-0.5 bg-ink-50 self-end mb-1.5" title={t('pl.view_hint', { defaultValue: 'Switch between your management hierarchy and the statutory 損益計算書 structure' })}>
            {(['management', 'statutory'] as const).map((v) => (
              <button key={v} type="button" onClick={() => setView(v)}
                className={'px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ' + (v === view ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900')}>
                {t(`pl.view_${v}`, { defaultValue: v === 'management' ? 'Management' : 'Statutory' })}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-ink-700 pb-1.5" title={t('pl.consolidated_hint', { defaultValue: 'Sum all entities, FX-translated to the reporting currency' })}>
            <input type="checkbox" checked={consolidated} onChange={(e) => setConsolidated(e.target.checked)} className="h-3.5 w-3.5 rounded border-border" />
            {t('pl.consolidated', { defaultValue: 'Consolidated' })}
          </label>
          <Field label={t('pl.reporting_currency', { defaultValue: 'Currency' })}>
            <CurrencyPicker value={reportingCurrency} onChange={setCurrency} className={`num ${inputCls} w-36`} />
          </Field>
          <button onClick={() => setShowEditor((v) => !v)} className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-md border border-border hover:bg-foreground/[0.04]">
            {showEditor ? t('pl.hide_editor', { defaultValue: 'Hide structure' }) : t('pl.edit_structure', { defaultValue: 'Edit structure' })}
          </button>
        </div>

        {showEditor && wsId && <HierarchyEditor wsId={wsId} t={t} />}

        {pl.data && pl.data.fx.missingRates.length > 0 && (
          <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs">
            {t('pl.fx_missing', { defaultValue: 'Missing FX rates (rows excluded): ' })}
            {pl.data.fx.missingRates.map((p) => `${p.from}→${p.to}`).join(', ')}
          </div>
        )}

        {/* P&L table */}
        {planErr ? (
          <div className="rounded-md bg-warn-500/10 border border-warn-500/30 text-warn-600 px-3 py-2 text-sm">{planErr}</div>
        ) : pl.isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : pl.data ? (
          <div className="rounded-md border border-border bg-card overflow-x-auto">
            <table className="w-full text-[13px] min-w-[640px]">
              <thead>
                <tr className="text-muted-foreground border-b border-border bg-foreground/[0.02] text-[10.5px] uppercase tracking-[0.14em]">
                  <th className="py-2 px-4 text-left font-medium">{t('pl.account', { defaultValue: 'Account' })}</th>
                  <th className="py-2 px-3 text-right font-medium">{scenarioA}</th>
                  {bva && <th className="py-2 px-3 text-right font-medium">{scenarioB}</th>}
                  {bva && <th className="py-2 px-3 text-right font-medium">{t('pl.delta', { defaultValue: 'Δ' })}</th>}
                  {bva && <th className="py-2 px-3 text-right font-medium">%</th>}
                </tr>
              </thead>
              <tbody>
                {pl.data.rows.map((r) => <PLRow key={r.id} r={r} bva={bva} fmt={fmt} onDrill={() => r.code && setDrill({ code: r.code, scenario: scenarioA, label: r.label })} />)}
                <tr className="border-t-2 border-border bg-foreground/[0.03] font-semibold">
                  <td className="py-2 px-4">{t('pl.total', { defaultValue: 'Total' })} ({pl.data.reportingCurrency})</td>
                  <td className="py-2 px-3 num text-right tabular-nums">{fmt(pl.data.totals.a)}</td>
                  {bva && <td className="py-2 px-3 num text-right tabular-nums">{pl.data.totals.b !== null ? fmt(pl.data.totals.b) : '—'}</td>}
                  {bva && <td colSpan={2} />}
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </main>

      {drill && wsId && <FactsDrawer wsId={wsId} code={drill.code} scenario={drill.scenario} label={drill.label} periodStart={periodStart} periodEnd={periodEnd} onClose={() => setDrill(null)} t={t} fmt={fmt} />}
    </AppShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{label}</span>
      {children}
    </label>
  );
}

function PLRow({ r, bva, fmt, onDrill }: { r: PlRow; bva: boolean; fmt: (n: number) => string; onDrill: () => void }) {
  const isLeaf = r.kind !== 'section' && !!r.code;
  return (
    <tr className={'border-b border-border/50 ' + (r.isSubtotal ? 'bg-foreground/[0.025] font-semibold' : 'hover:bg-foreground/[0.02]')}>
      <td className="py-1.5 px-4">
        <span style={{ paddingLeft: `${r.level * 16}px` }} className="inline-flex items-center gap-1.5">
          {isLeaf ? (
            <button onClick={onDrill} className="text-foreground hover:text-emerald-700 hover:underline text-left">
              {r.code && <span className="num text-[11px] text-muted-foreground mr-1.5">{r.code}</span>}{r.label}
            </button>
          ) : (
            <span className="text-ink-900">{r.label}</span>
          )}
        </span>
      </td>
      <td className="py-1.5 px-3 num text-right tabular-nums text-foreground">{fmt(r.amountA)}</td>
      {bva && <td className="py-1.5 px-3 num text-right tabular-nums text-muted-foreground">{r.amountB !== null ? fmt(r.amountB) : '—'}</td>}
      {bva && <td className={'py-1.5 px-3 num text-right tabular-nums ' + (r.delta === null ? 'text-muted-foreground' : r.delta >= 0 ? 'text-emerald-700' : 'text-warn-700')}>{r.delta !== null ? fmt(r.delta) : '—'}</td>}
      {bva && <td className="py-1.5 px-3 num text-right tabular-nums text-muted-foreground">{r.deltaPct !== null ? `${r.deltaPct.toFixed(1)}%` : '—'}</td>}
    </tr>
  );
}

// ---- Hierarchy editor ------------------------------------------------------
function HierarchyEditor({ wsId, t }: { wsId: string; t: (k: string, o?: Record<string, unknown>) => string }) {
  const qc = useQueryClient();
  const { data: nodes = [] } = useQuery({ queryKey: ['accounts', wsId], queryFn: () => accountsApi.list(wsId) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['accounts', wsId] }); qc.invalidateQueries({ queryKey: ['management-pl', wsId] }); };

  const addSection = useMutation({ mutationFn: () => accountsApi.create(wsId, { label: t('pl.new_section', { defaultValue: 'New section' }), kind: 'section', isSubtotal: true, sortOrder: nodes.length }), onSuccess: invalidate });
  const update = useMutation({ mutationFn: (v: { id: string; patch: Parameters<typeof accountsApi.update>[2] }) => accountsApi.update(wsId, v.id, v.patch), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => accountsApi.remove(wsId, id), onSuccess: invalidate });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const sections = nodes.filter((n) => n.kind === 'section');
  const errMsg = (update.error || remove.error) ? (((update.error || remove.error) as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error') : null;

  return (
    <>
    <PageSection eyebrow={t('pl.editor_eyebrow', { defaultValue: 'Account structure' })} title={t('pl.editor_title', { defaultValue: 'Hierarchy editor' })}
      actions={<button onClick={() => addSection.mutate()} className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"><Plus className="h-3.5 w-3.5" /> {t('pl.add_section', { defaultValue: 'Add section' })}</button>}>
      {errMsg && <p className="mb-2 text-xs text-warn-700">{errMsg}</p>}
      <div className="rounded-md border border-border bg-white divide-y divide-border/60">
        {nodes.map((n: AccountNode) => (
          <div key={n.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
            <input
              defaultValue={n.label}
              onBlur={(e) => e.target.value !== n.label && update.mutate({ id: n.id, patch: { label: e.target.value } })}
              className={'flex-1 min-w-0 bg-transparent px-1 py-0.5 rounded ' + (n.kind === 'section' ? 'font-semibold text-ink-900' : 'text-ink-700')}
            />
            {n.code && <span className="num text-[10px] text-muted-foreground shrink-0">{n.code}</span>}
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">{n.kind}</span>
            {/* parent (move) */}
            <select value={n.parent_id ?? ''} onChange={(e) => update.mutate({ id: n.id, patch: { parentId: e.target.value || null } })} className="rounded border border-border bg-white px-1.5 py-0.5 text-[11px] shrink-0 max-w-[160px]">
              <option value="">{t('pl.top_level', { defaultValue: '— top level —' })}</option>
              {sections.filter((s) => s.id !== n.id).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            {/* statement type (Part 11 — drives consolidation FX rate kind) */}
            {n.code && (
              <select value={n.statement_type ?? ''} onChange={(e) => update.mutate({ id: n.id, patch: { statementType: (e.target.value || null) as 'pl' | 'asset' | 'liability' | 'equity' | null } })} title={t('pl.statement_type_hint', { defaultValue: 'Statement type — sets the FX rate kind in consolidation (P&L→avg, BS→period-end, equity→historical)' })} className="rounded border border-border bg-white px-1 py-0.5 text-[10px] shrink-0">
                <option value="">{t('pl.stmt_none', { defaultValue: '— type —' })}</option>
                <option value="pl">{t('pl.stmt_pl', { defaultValue: 'P&L' })}</option>
                <option value="asset">{t('pl.stmt_asset', { defaultValue: 'Asset' })}</option>
                <option value="liability">{t('pl.stmt_liability', { defaultValue: 'Liability' })}</option>
                <option value="equity">{t('pl.stmt_equity', { defaultValue: 'Equity' })}</option>
              </select>
            )}
            {/* statutory category (W3-4 — drives the statutory 損益計算書 view) */}
            {n.code && (
              <select value={n.statutory_category ?? ''} onChange={(e) => update.mutate({ id: n.id, patch: { statutoryCategory: (e.target.value || null) as StatutoryCategory | null } })} title={t('pl.statutory_hint', { defaultValue: 'Statutory category — where this account appears in the legal 損益計算書 view' })} className="rounded border border-border bg-white px-1 py-0.5 text-[10px] shrink-0">
                <option value="">{t('pl.stat_none', { defaultValue: '— statutory —' })}</option>
                <option value="revenue">{t('pl.stat_revenue', { defaultValue: '売上高 Revenue' })}</option>
                <option value="cogs">{t('pl.stat_cogs', { defaultValue: '売上原価 COGS' })}</option>
                <option value="opex">{t('pl.stat_opex', { defaultValue: '販管費 SG&A' })}</option>
                <option value="non_op_income">{t('pl.stat_non_op_income', { defaultValue: '営業外収益' })}</option>
                <option value="non_op_expense">{t('pl.stat_non_op_expense', { defaultValue: '営業外費用' })}</option>
                <option value="extra_income">{t('pl.stat_extra_income', { defaultValue: '特別利益' })}</option>
                <option value="extra_loss">{t('pl.stat_extra_loss', { defaultValue: '特別損失' })}</option>
                <option value="tax">{t('pl.stat_tax', { defaultValue: '法人税等 Tax' })}</option>
              </select>
            )}
            {/* sign toggle */}
            <button onClick={() => update.mutate({ id: n.id, patch: { sign: n.sign === 1 ? -1 : 1 } })} title={t('pl.sign_hint', { defaultValue: 'Sign used when this rolls into its parent subtotal' })} className={'num w-6 text-center rounded shrink-0 ' + (n.sign === 1 ? 'text-emerald-700' : 'text-warn-700')}>{n.sign === 1 ? '+' : '−'}</button>
            <button onClick={() => setDeleteId(n.id)} className="text-ink-400 hover:text-warn-600 shrink-0" aria-label={t('common.delete')}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">{t('pl.editor_hint', { defaultValue: 'Move accounts into sections; set a section sign to − to subtract it (e.g. costs). Deleting an account with facts just unmaps it.' })}</p>
    </PageSection>
    <ConfirmModal
      open={deleteId !== null}
      title={t('common.delete')}
      confirmLabel={t('common.delete')}
      destructive
      onConfirm={() => { if (deleteId) remove.mutate(deleteId); setDeleteId(null); }}
      onCancel={() => setDeleteId(null)}
    />
    </>
  );
}

// ---- Facts drill drawer ----------------------------------------------------
function FactsDrawer({ wsId, code, scenario, label, periodStart, periodEnd, onClose, t, fmt }: {
  wsId: string; code: string; scenario: string; label: string; periodStart: string; periodEnd: string;
  onClose: () => void; t: (k: string, o?: Record<string, unknown>) => string; fmt: (n: number) => string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['facts-range', wsId, code, scenario, periodStart, periodEnd],
    queryFn: async () => (await api.get<{ rows: { factId: string; periodStart: string; periodEnd: string; amount: number; currency: string }[] }>(
      `/api/workspaces/${wsId}/facts-range`, { params: { periodStart, periodEnd, accountCode: code, scenario } },
    )).data.rows,
  });
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md h-full bg-background border-l border-border shadow-editorial flex flex-col">
        <header className="h-14 px-5 flex items-center justify-between border-b border-border">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{label}</p>
            <p className="text-[11px] text-muted-foreground num">{code} · {scenario}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            : !data || data.length === 0 ? <p className="text-sm text-muted-foreground">—</p>
            : data.map((f) => (
              <div key={f.factId} className="flex items-center justify-between text-[12.5px] rounded border border-border/60 px-2.5 py-1.5">
                <span className="num text-muted-foreground">{f.periodStart} → {f.periodEnd}</span>
                <span className="num tabular-nums text-foreground">{fmt(f.amount)} {f.currency}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
